/**
 * Escanear un código QR, desde el móvil o desde el ordenador
 * -----------------------------------------------------------------------------
 * Un botón. Lo que hace al pulsarlo depende de dónde esté:
 *
 * - **En un móvil** abre la cámara ahí mismo y lee el código.
 * - **En un ordenador** enseña un QR en pantalla. Se escanea con el móvil, que
 *   abre una página con un botón de escanear, y lo que lea aparece **en el
 *   ordenador** sin tocar nada más.
 *
 * Lo segundo existe porque casi ningún ordenador de oficina tiene cámara
 * apuntando a un papel, y el papel está encima de la mesa. El móvil sí, y ya lo
 * lleva quien registra la factura en la mano.
 *
 *   <div data-company-escaner
 *        data-company-escaner-destino="cufe"
 *        data-company-escaner-turno="/facturas/escaneo/nuevo"
 *        data-company-escaner-estado="/facturas/escaneo/000...000">
 *     <button type="button" data-company-escaner-boton class="btn btn-outline-primary">
 *       Escanear el QR
 *     </button>
 *   </div>
 *
 * `destino` es el `id` del campo que se rellena. `turno` es a dónde se pide uno,
 * y `estado` a dónde se pregunta si el móvil ya escaneó -generada con un token
 * de mentira, porque el enrutador exige uno con la forma buena; aquí se cambia
 * el último tramo por el de verdad-.
 *
 * ## Cómo se lee el código
 *
 * Con `BarcodeDetector` si el navegador lo trae -Chrome y Android lo tienen y va
 * en el propio sistema, sin descargar nada- y si no, con un lector incluido en
 * el tema. Hacen falta los dos: Safari en iPhone no trae el primero, y en una
 * bodega hay de todo.
 *
 * ## Lo que NO hace
 *
 * **No rellena la factura entera.** El QR de las facturas de ahora lleva solo el
 * CUFE, dentro de una dirección de la DIAN: el CUFE es una huella calculada a
 * partir de los datos, no un envoltorio con los datos dentro. Esto ahorra
 * teclear noventa y seis caracteres a mano, que no es poco; para el resto está
 * el XML que manda el proveedor.
 */

import QRCode from 'qrcode';
import { notify } from './notify.js';

const ATTR = 'data-company-escaner';

/**
 * El lector de reserva, cargado **solo cuando hace falta**.
 *
 * Pesa 250 kB y no viene en el paquete a propósito: meterlo dentro obligaría a
 * descargarlo en cada página de la suite para una pantalla. Se trae al pulsar
 * «escanear», y solo si el navegador no trae lector propio -hoy eso es Safari en
 * iPhone; Chrome y Android leen códigos de serie con `BarcodeDetector`-.
 *
 * @returns {Promise<?Function>}
 */
let cargandoLector = null;

function traerElLector() {
  if (window.jsQR) return Promise.resolve(window.jsQR);
  if (cargandoLector) return cargandoLector;

  // Junto al guion del tema, que es de donde cuelga todo lo suyo. Se saca de su
  // propia dirección en vez de escribirla: cada aplicación lo publica en su
  // sitio y aquí no se sabe cuál es.
  const suyo = document.querySelector('script[src*="company-theme"]');
  const base = suyo ? suyo.src.replace(/[^/]+$/, '') : '';

  // Y con su misma versión detrás. Las aplicaciones le ponen `?v=` al guion del
  // tema porque el fichero se llama siempre igual y el navegador se queda con el
  // que ya tenía: sin esto, el día que este cambie nadie lo verá cambiar.
  const sello = suyo && suyo.src.includes('?') ? suyo.src.slice(suyo.src.indexOf('?')) : '';

  cargandoLector = new Promise((listo) => {
    const guion = document.createElement('script');
    guion.src = `${base}jsqr.js${sello}`;
    guion.onload = () => listo(window.jsQR || null);
    guion.onerror = () => listo(null);
    document.head.appendChild(guion);
  });

  return cargandoLector;
}

/** Cada cuánto se le pregunta al servidor si el móvil ya escaneó. */
const CADA = 1500;

/** Cuánto se espera al móvil antes de rendirse. */
const ESPERA_MAXIMA = 120000;

/**
 * ¿Esto es un móvil?
 *
 * Por el puntero y no por el ancho: un portátil con pantalla pequeña sigue
 * teniendo ratón y su cámara mira a la cara, no al papel. Lo que decide es si se
 * toca con el dedo, que es lo que acompaña a una cámara trasera.
 */
function esMovil() {
  if (typeof window.matchMedia !== 'function') return false;

  return window.matchMedia('(pointer: coarse)').matches && navigator.maxTouchPoints > 0;
}

/**
 * Lee un fotograma y devuelve el texto del código, o `null`.
 *
 * @param {HTMLVideoElement} video
 * @param {HTMLCanvasElement} lienzo
 * @param {any} detector
 * @returns {Promise<?string>}
 */
async function leerFotograma(video, lienzo, detector) {
  if (!video.videoWidth) return null;

  if (detector) {
    try {
      const encontrados = await detector.detect(video);

      if (encontrados.length > 0) return String(encontrados[0].rawValue || '');
    } catch (error) {
      // El detector nativo falla en algunos aparatos a mitad de la sesión. Se
      // sigue con el lector propio en vez de dejar la cámara abierta sin leer.
    }
  }

  const lector = await traerElLector();

  if (!lector) return null;

  lienzo.width = video.videoWidth;
  lienzo.height = video.videoHeight;

  const pincel = lienzo.getContext('2d', { willReadFrequently: true });

  if (!pincel) return null;

  pincel.drawImage(video, 0, 0, lienzo.width, lienzo.height);

  const datos = pincel.getImageData(0, 0, lienzo.width, lienzo.height);
  const codigo = lector(datos.data, datos.width, datos.height, { inversionAttempts: 'dontInvert' });

  return codigo ? codigo.data : null;
}

/**
 * Abre la cámara y devuelve lo que lea, o `null` si se cierra sin leer.
 *
 * @returns {Promise<?string>}
 */
export async function escanearConLaCamara() {
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    notify.error('Este navegador no deja usar la cámara. Escribe el código a mano.');

    return null;
  }

  const capa = document.createElement('div');
  capa.className = 'owari-escaner';
  capa.innerHTML =
    '<video playsinline muted></video>' +
    '<div class="owari-escaner-marco"></div>' +
    '<p class="owari-escaner-texto">Apunta al código de la factura</p>' +
    '<button type="button" class="btn btn-light owari-escaner-cerrar">Cancelar</button>';

  document.body.appendChild(capa);

  const video = capa.querySelector('video');
  const lienzo = document.createElement('canvas');
  let flujo = null;

  const cerrar = () => {
    if (flujo) flujo.getTracks().forEach((pista) => pista.stop());
    capa.remove();
  };

  return new Promise((listo) => {
    let acabado = false;

    const terminar = (texto) => {
      if (acabado) return;
      acabado = true;
      cerrar();
      listo(texto);
    };

    capa.querySelector('.owari-escaner-cerrar').addEventListener('click', () => terminar(null));

    navigator.mediaDevices
      // La trasera: la de delante mira a la cara y el papel está en la mesa.
      .getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then(async (obtenido) => {
        flujo = obtenido;
        video.srcObject = flujo;
        await video.play();

        // El detector nativo, **si se deja construir**. Hay navegadores que lo
        // enseñan y revientan al pedirle `qr_code`, y ese error caía en el
        // `catch` de abajo: la cámara se cerraba diciendo «revisa el permiso»,
        // que es mentira y manda a hurgar donde no es. Sin detector se sigue
        // con el lector de reserva, que es justo para lo que está.
        let detector = null;

        try {
          if ('BarcodeDetector' in window) {
            detector = new window.BarcodeDetector({ formats: ['qr_code'] });
          }
        } catch (error) {
          detector = null;
        }

        const mirar = async () => {
          if (acabado) return;

          const texto = await leerFotograma(video, lienzo, detector);

          if (texto) {
            terminar(texto);

            return;
          }

          requestAnimationFrame(mirar);
        };

        mirar();
      })
      .catch(() => {
        notify.error('No se pudo abrir la cámara. Revisa el permiso del navegador.');
        terminar(null);
      });
  });
}

class Escaner {
  constructor(raiz) {
    this.raiz = raiz;
    this.destino = document.getElementById(raiz.dataset.companyEscanerDestino || '');
    // Las dos direcciones enteras, no una base a la que pegarle trozos: cada
    // aplicación las genera con su enrutador, y componerlas aquí a mano obliga
    // a suponer cómo se llaman por dentro.
    this.turno = raiz.dataset.companyEscanerTurno || '';
    this.estado = raiz.dataset.companyEscanerEstado || '';
    // Opcional: a quién pulsar cuando ya está puesto lo leído. Un QR de factura
    // no trae un dato sino nueve, y repartirlos por sus casillas es cosa de
    // quien sepa leerlos. Aquí solo se deja el texto y se avisa.
    this.envia = document.getElementById(raiz.dataset.companyEscanerEnvia || '');

    const boton = raiz.querySelector('[data-company-escaner-boton]');

    if (!boton || !this.destino) return;

    boton.addEventListener('click', () => this.pulsado());
  }

  async pulsado() {
    const texto = esMovil() ? await escanearConLaCamara() : await this.porElMovil();

    if (texto) this.poner(texto);
  }

  /**
   * Escribe lo leído y avisa a quien escuche.
   *
   * El `input` y el `change` a mano: el campo se rellena por código, y sin ellos
   * ni el rótulo flotante se entera ni se dispara nada que dependa del valor.
   */
  poner(texto) {
    this.destino.value = texto;
    this.destino.dispatchEvent(new Event('input', { bubbles: true }));
    this.destino.dispatchEvent(new Event('change', { bubbles: true }));

    // Si hay a quién pulsar, se pulsa y punto: el aviso se lo lleva por delante
    // la recarga, y quien procese lo leído dirá qué salió, que es más útil que
    // «código leído».
    if (this.envia) {
      this.envia.click();

      return;
    }

    notify.success('Código leído. Revisa lo que salió antes de guardar.');
  }

  /**
   * El relevo: aquí se enseña un QR, el móvil lo escanea y devuelve lo que lea.
   *
   * @returns {Promise<?string>}
   */
  async porElMovil() {
    // Sin relevo montado en el servidor no hay a quién preguntar. Se dice qué
    // hacer en vez de llamar a una dirección que no existe y fallar en silencio.
    if ('' === this.turno || '' === this.estado) {
      notify.warning(
        'Desde el ordenador hace falta el móvil. Abre esta pantalla en él, o pega el código a mano.'
      );

      return null;
    }

    let turno;

    try {
      const respuesta = await fetch(this.turno, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      if (!respuesta.ok) throw new Error('sin turno');

      turno = await respuesta.json();
    } catch (error) {
      notify.error('No se pudo preparar el escaneo. Inténtalo otra vez.');

      return null;
    }

    return this.esperarAlMovil(turno);
  }

  /**
   * @returns {Promise<?string>}
   */
  async esperarAlMovil(turno) {
    const capa = document.createElement('div');
    capa.className = 'owari-escaner owari-escaner-relevo';
    capa.innerHTML =
      '<div class="owari-escaner-tarjeta">' +
      '<h3>Escanéalo con el móvil</h3>' +
      '<p class="text-muted">Se abrirá una página con un botón para leer el QR de la ' +
      'factura. Lo que lea aparece aquí solo.</p>' +
      '<canvas></canvas>' +
      '<p class="owari-escaner-espera">Esperando al móvil…</p>' +
      '<button type="button" class="btn btn-outline-primary owari-escaner-cerrar">Cancelar</button>' +
      '</div>';

    document.body.appendChild(capa);

    await QRCode.toCanvas(capa.querySelector('canvas'), turno.url, { width: 220, margin: 1 });

    return new Promise((listo) => {
      let acabado = false;
      const desde = Date.now();

      const terminar = (texto) => {
        if (acabado) return;
        acabado = true;
        clearInterval(reloj);
        capa.remove();
        listo(texto);
      };

      capa.querySelector('.owari-escaner-cerrar').addEventListener('click', () => terminar(null));

      const reloj = setInterval(async () => {
        if (Date.now() - desde > ESPERA_MAXIMA) {
          notify.error('El móvil no contestó. Vuelve a intentarlo.');
          terminar(null);

          return;
        }

        try {
          // Se cambia el último tramo por el turno de verdad. La plantilla
          // genera esa dirección con un token de mentira -su enrutador exige
          // uno con la forma correcta- y aquí se sustituye.
          const respuesta = await fetch(this.estado.replace(/[^/]+$/, turno.token), {
            headers: { Accept: 'application/json' },
          });

          if (!respuesta.ok) return;

          const estado = await respuesta.json();

          if (estado.listo) terminar(String(estado.contenido || ''));
        } catch (error) {
          // Un corte de red no acaba con la espera: el móvil puede estar a
          // punto de contestar y rendirse aquí obligaría a empezar de nuevo.
        }
      }, CADA);
    });
  }
}

/**
 * Monta los escáneres que haya dentro de `raiz`.
 *
 * @param {ParentNode} [raiz=document]
 */
export function setupEscaneres(raiz = document) {
  if (!raiz || typeof raiz.querySelectorAll !== 'function') return;

  raiz.querySelectorAll(`[${ATTR}]`).forEach((nodo) => {
    if (nodo.dataset.companyEscanerMontado) return;

    nodo.dataset.companyEscanerMontado = '1';
    // Colgado del nodo, como el resto de componentes del tema (`__companyFile`,
    // `__companyCard`): sin esto no hay forma de tocarlo desde fuera, ni para
    // usarlo desde una pantalla ni para probarlo sin cámara.
    nodo.__companyEscaner = new Escaner(nodo);
  });
}

export default setupEscaneres;
